import { useMemo, type ComponentType, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertCircle,
  Banknote,
  CheckCircle2,
  CreditCard,
  IndianRupee,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge, type Status } from '@/components/StatusBadge';
import { useDashboardContext } from '@/contexts/DashboardContext';
import { useMemberContext } from '@/contexts/MemberContext';
import { usePaymentContext } from '@/contexts/PaymentContext';
import {
  formatCurrency,
  getCurrentMonthLabel,
  MONTHLY_FEE,
} from '@/lib/payments';
import { formatDateDisplay } from '@/lib/dates';
import {
  getLifetimeCollected,
  getMemberGrowthTrend,
  getMembershipBreakdown,
  getMonthlyCollectionTrend,
  getRecentPayments,
  getTotalOutstanding,
  getTrendRangeLabel,
  getUnpaidActiveMembers,
  type MonthlyCollectionPoint,
  type MemberGrowthPoint,
} from '@/lib/dashboard';

const collectionChartConfig = {
  collected: { label: 'Received amount', color: '#4369a0' },
  expected: { label: 'Total amount', color: '#d1d5db' },
} satisfies ChartConfig;

const growthChartConfig = {
  totalMembers: { label: 'Total members', color: '#4369a0' },
  newMembers: { label: 'New members', color: '#0d9488' },
} satisfies ChartConfig;

const membershipChartConfig = {
  active: { label: 'Active', color: '#059669' },
  completed: { label: 'Completed', color: '#4369a0' },
  closed: { label: 'Closed', color: '#9ca3af' },
} satisfies ChartConfig;

const paymentMethodChartConfig = {
  cash: { label: 'Cash', color: '#0d9488' },
  online: { label: 'Online', color: '#4369a0' },
} satisfies ChartConfig;

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  tone,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
  hint?: string;
  tone: 'brand' | 'emerald' | 'amber' | 'gray' | 'teal';
}) {
  const toneStyles = {
    brand: 'border-[#4369a0]/15 bg-[#4369a0]/5',
    emerald: 'border-emerald-100 bg-emerald-50/60',
    amber: 'border-amber-100 bg-amber-50/60',
    gray: 'border-gray-200 bg-white',
    teal: 'border-teal-100 bg-teal-50/60',
  }[tone];

  const iconStyles = {
    brand: 'bg-[#4369a0]/10 text-[#4369a0]',
    emerald: 'bg-emerald-100 text-emerald-700',
    amber: 'bg-amber-100 text-amber-700',
    gray: 'bg-gray-100 text-gray-600',
    teal: 'bg-teal-100 text-teal-700',
  }[tone];

  return (
    <div className={`rounded-xl border p-4 shadow-sm ${toneStyles}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-500">{label}</p>
          <p className="mt-1 truncate text-2xl font-semibold tabular-nums text-gray-900">
            {value}
          </p>
          {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
        </div>
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${iconStyles}`}
        >
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function SectionCard({
  title,
  description,
  children,
  className,
  fillHeight = false,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  fillHeight?: boolean;
}) {
  return (
    <Card className={`h-full min-h-0 border-gray-200/80 bg-white shadow-sm ${className ?? ''}`}>
      <CardContent
        className={`p-5 ${fillHeight ? 'flex min-h-0 flex-1 flex-col' : ''}`}
      >
        <div className="mb-4 shrink-0">
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
        {fillHeight ? (
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function MonthlyCollectionTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: MonthlyCollectionPoint }>;
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs shadow-lg">
      <p className="mb-2 font-semibold text-gray-900">{point.month}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-8">
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-gray-300" />
            Total :
          </span>
          <span className="font-semibold tabular-nums text-gray-900">
            {formatCurrency(point.expected)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-8">
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-[#4369a0]" />
            Received :
          </span>
          <span className="font-semibold tabular-nums text-gray-900">
            {formatCurrency(point.collected)}
          </span>
        </div>
      </div>
    </div>
  );
}

function CollectionChartLegend() {
  return (
    <div className="mt-2 shrink-0 flex flex-wrap items-center justify-center gap-5 text-xs text-gray-600">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm bg-gray-300" />
        Total amount
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm bg-[#4369a0]" />
        Received amount
      </span>
    </div>
  );
}

function MemberGrowthTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: MemberGrowthPoint }>;
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs shadow-lg">
      <p className="mb-2 font-semibold text-gray-900">{point.month}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-8">
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-[#4369a0]" />
            Total members :
          </span>
          <span className="font-semibold tabular-nums text-gray-900">
            {point.totalMembers}
          </span>
        </div>
        <div className="flex items-center justify-between gap-8">
          <span className="flex items-center gap-1.5 text-gray-600">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm bg-[#0d9488]" />
            New members :
          </span>
          <span className="font-semibold tabular-nums text-gray-900">
            {point.newMembers}
          </span>
        </div>
      </div>
    </div>
  );
}

function GrowthChartLegend() {
  return (
    <div className="mt-2 shrink-0 flex flex-wrap items-center justify-center gap-5 text-xs text-gray-600">
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm bg-[#4369a0]" />
        Total members
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm bg-[#0d9488]" />
        New members
      </span>
    </div>
  );
}

function MetricRow({
  label,
  value,
  progress,
  tone = 'brand',
}: {
  label: string;
  value: string;
  progress?: number;
  tone?: 'brand' | 'emerald' | 'amber';
}) {
  const barClass = {
    brand: '[&>div]:bg-[#4369a0]',
    emerald: '[&>div]:bg-emerald-500',
    amber: '[&>div]:bg-amber-500',
  }[tone];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-semibold tabular-nums text-gray-900">{value}</span>
      </div>
      {progress !== undefined && (
        <Progress value={progress} className={`h-2 bg-gray-100 ${barClass}`} />
      )}
    </div>
  );
}

export default function Dashboard() {
  const { getDashboardStats } = useDashboardContext();
  const { members } = useMemberContext();
  const { payments } = usePaymentContext();
  const stats = getDashboardStats();

  const currentMonthLabel = useMemo(() => getCurrentMonthLabel(), []);

  const collectionTrend = useMemo(
    () => getMonthlyCollectionTrend(payments, members.length, 6),
    [payments, members.length]
  );

  const memberGrowth = useMemo(() => getMemberGrowthTrend(members, 6), [members]);

  const membershipBreakdown = useMemo(
    () => getMembershipBreakdown(members, payments),
    [members, payments]
  );

  const lifetimeCollected = useMemo(() => getLifetimeCollected(payments), [payments]);
  const totalOutstanding = useMemo(
    () => getTotalOutstanding(members, payments),
    [members, payments]
  );

  const unpaidMembers = useMemo(
    () => getUnpaidActiveMembers(members, payments, 6),
    [members, payments]
  );

  const recentPayments = useMemo(
    () => getRecentPayments(payments, members, 8),
    [payments, members]
  );

  const collectionRate = useMemo(
    () =>
      stats.totalMembers > 0
        ? Math.round((stats.paidCustomers / stats.totalMembers) * 100)
        : 0,
    [stats.totalMembers, stats.paidCustomers]
  );

  const monthCollectionRate = useMemo(
    () =>
      stats.expectedAmount > 0
        ? Math.round((stats.receivedAmount / stats.expectedAmount) * 100)
        : 0,
    [stats.expectedAmount, stats.receivedAmount]
  );

  const membershipChartData = useMemo(
    () =>
      [
        { key: 'active', value: membershipBreakdown.active, fill: 'var(--color-active)' },
        { key: 'completed', value: membershipBreakdown.completed, fill: 'var(--color-completed)' },
        { key: 'closed', value: membershipBreakdown.closed, fill: 'var(--color-closed)' },
      ].filter((item) => item.value > 0),
    [membershipBreakdown]
  );

  const paymentMethodData = useMemo(
    () =>
      [
        { key: 'cash', value: stats.cashReceived, fill: 'var(--color-cash)' },
        { key: 'online', value: stats.onlineReceived, fill: 'var(--color-online)' },
      ].filter((item) => item.value > 0),
    [stats.cashReceived, stats.onlineReceived]
  );

  const cashShare = stats.receivedAmount
    ? Math.round((stats.cashReceived / stats.receivedAmount) * 100)
    : 0;
  const onlineShare = stats.receivedAmount
    ? Math.round((stats.onlineReceived / stats.receivedAmount) * 100)
    : 0;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50/80">
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
        <div className="p-5">
          {/* Header */}
          <div className="mb-6 shrink-0">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
                <p className="mt-0.5 text-sm text-gray-500">
                  Financial overview and membership health for {currentMonthLabel}
                </p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-xs">
                <TrendingUp className="h-4 w-4 text-[#4369a0]" />
                <div className="text-right">
                  <p className="text-xs text-gray-500">This month collection</p>
                  <p className="text-sm font-semibold tabular-nums text-gray-900">
                    {monthCollectionRate}% of target
                  </p>
                </div>
              </div>
            </div>
            <div className="h-px bg-gray-200/80" />
          </div>

          {/* KPI strip */}
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <KpiCard
              icon={Users}
              label="Total Members"
              value={String(stats.totalMembers)}
              hint="Registered in the system"
              tone="brand"
            />
            <KpiCard
              icon={CheckCircle2}
              label="Paid This Month"
              value={String(stats.paidCustomers)}
              hint={`${collectionRate}% collection rate`}
              tone="emerald"
            />
            <KpiCard
              icon={AlertCircle}
              label="Unpaid This Month"
              value={String(stats.unpaidCustomers)}
              hint="Requires follow-up"
              tone="amber"
            />
            <KpiCard
              icon={IndianRupee}
              label="Collected This Month"
              value={formatCurrency(stats.receivedAmount)}
              hint={`Target ${formatCurrency(stats.expectedAmount)}`}
              tone="teal"
            />
            <KpiCard
              icon={Wallet}
              label="Lifetime Collected"
              value={formatCurrency(lifetimeCollected)}
              hint="All recorded payments"
              tone="gray"
            />
            <KpiCard
              icon={UserPlus}
              label="New Members"
              value={String(stats.monthlyNewMembers)}
              hint="Joined this month"
              tone="brand"
            />
          </div>

          {/* Charts row */}
          <div className="mb-6 grid grid-cols-1 gap-5 xl:grid-cols-12">
            <SectionCard
              title="Monthly Collection"
              description="Collected vs expected dues over the last 6 months"
              className="xl:col-span-8"
              fillHeight
            >
              <ChartContainer
                config={collectionChartConfig}
                className="relative min-h-[280px] w-full flex-1 !aspect-auto !justify-start [&_.recharts-responsive-container]:!absolute [&_.recharts-responsive-container]:!inset-0 [&_.recharts-responsive-container]:!h-full [&_.recharts-responsive-container]:!w-full"
              >
                <BarChart
                  data={collectionTrend}
                  margin={{ top: 4, right: 12, left: 8, bottom: 4 }}
                  barGap={3}
                  barCategoryGap="12%"
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-200" />
                  <XAxis
                    dataKey="shortMonth"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={6}
                    interval={0}
                    className="text-[11px] fill-gray-500"
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={4}
                    width={60}
                    domain={[0, 'dataMax']}
                    className="text-[11px] fill-gray-500"
                    tickFormatter={(value) => `₹${value / 1000}k`}
                  />
                  <ChartTooltip
                    cursor={{ fill: 'rgba(67, 105, 160, 0.06)' }}
                    content={<MonthlyCollectionTooltip />}
                  />
                  <Bar dataKey="expected" fill="var(--color-expected)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="collected" fill="var(--color-collected)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
              <CollectionChartLegend />
            </SectionCard>

            <SectionCard
              title="Financial Health"
              description="Current month performance snapshot"
              className="xl:col-span-4"
            >
              <div className="space-y-5">
                <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Collection rate</span>
                    <span className="text-2xl font-bold tabular-nums text-[#4369a0]">
                      {monthCollectionRate}%
                    </span>
                  </div>
                  <Progress value={monthCollectionRate} className="h-2.5 bg-gray-200 [&>div]:bg-[#4369a0]" />
                  <p className="mt-2 text-xs text-gray-500">
                    {formatCurrency(stats.receivedAmount)} received of{' '}
                    {formatCurrency(stats.expectedAmount)} expected
                  </p>
                </div>

                <MetricRow
                  label="Pending this month"
                  value={formatCurrency(Math.max(0, stats.pendingAmount))}
                  progress={monthCollectionRate}
                  tone="amber"
                />
                <MetricRow
                  label="Total outstanding"
                  value={formatCurrency(totalOutstanding)}
                  tone="brand"
                />
                <MetricRow
                  label="Monthly due per member"
                  value={formatCurrency(MONTHLY_FEE)}
                  tone="emerald"
                />

                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-emerald-700">
                      <Banknote className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Cash</span>
                    </div>
                    <p className="text-sm font-semibold tabular-nums text-gray-900">
                      {formatCurrency(stats.cashReceived)}
                    </p>
                    <p className="text-xs text-gray-500">{cashShare}% share</p>
                  </div>
                  <div className="rounded-lg border border-[#4369a0]/15 bg-[#4369a0]/5 p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-[#4369a0]">
                      <CreditCard className="h-3.5 w-3.5" />
                      <span className="text-xs font-medium">Online</span>
                    </div>
                    <p className="text-sm font-semibold tabular-nums text-gray-900">
                      {formatCurrency(stats.onlineReceived)}
                    </p>
                    <p className="text-xs text-gray-500">{onlineShare}% share</p>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Second charts row */}
          <div className="mb-6 grid grid-cols-1 items-stretch gap-5 lg:grid-cols-3">
            <SectionCard
              title="Member Growth"
              description={`Total membership trend ${getTrendRangeLabel(6)}`}
              fillHeight
            >
              <ChartContainer
                config={growthChartConfig}
                className="relative min-h-[240px] w-full flex-1 !aspect-auto !justify-start [&_.recharts-responsive-container]:!absolute [&_.recharts-responsive-container]:!inset-0 [&_.recharts-responsive-container]:!h-full [&_.recharts-responsive-container]:!w-full"
              >
                <AreaChart data={memberGrowth} margin={{ top: 4, right: 12, left: 8, bottom: 4 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-gray-200" />
                  <XAxis
                    dataKey="shortMonth"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={6}
                    interval={0}
                    className="text-[11px] fill-gray-500"
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={4}
                    width={32}
                    className="text-[11px] fill-gray-500"
                  />
                  <ChartTooltip content={<MemberGrowthTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="totalMembers"
                    stroke="var(--color-totalMembers)"
                    fill="var(--color-totalMembers)"
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="newMembers"
                    stroke="var(--color-newMembers)"
                    fill="var(--color-newMembers)"
                    fillOpacity={0.1}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
              <GrowthChartLegend />
            </SectionCard>

            <SectionCard title="Membership Status" description="Active, completed, and closed accounts">
              {membershipChartData.length > 0 ? (
                <div className="flex flex-col items-center gap-4">
                  <ChartContainer config={membershipChartConfig} className="mx-auto h-[200px] w-full max-w-[220px]">
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                      <Pie
                        data={membershipChartData}
                        dataKey="value"
                        nameKey="key"
                        innerRadius={52}
                        outerRadius={78}
                        strokeWidth={2}
                      >
                        {membershipChartData.map((entry) => (
                          <Cell key={entry.key} fill={entry.fill} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                  <div className="grid w-full grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-emerald-50 px-2 py-2">
                      <p className="text-lg font-semibold text-emerald-800">{membershipBreakdown.active}</p>
                      <p className="text-xs text-gray-500">Active</p>
                    </div>
                    <div className="rounded-lg bg-[#4369a0]/10 px-2 py-2">
                      <p className="text-lg font-semibold text-[#2d4f73]">{membershipBreakdown.completed}</p>
                      <p className="text-xs text-gray-500">Completed</p>
                    </div>
                    <div className="rounded-lg bg-gray-100 px-2 py-2">
                      <p className="text-lg font-semibold text-gray-700">{membershipBreakdown.closed}</p>
                      <p className="text-xs text-gray-500">Closed</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="py-10 text-center text-sm text-gray-500">No membership data</p>
              )}
            </SectionCard>

            <SectionCard title="Payment Methods" description="Cash vs online this month">
              {paymentMethodData.length > 0 ? (
                <div className="flex flex-col items-center gap-4">
                  <ChartContainer
                    config={paymentMethodChartConfig}
                    className="mx-auto h-[200px] w-full max-w-[220px]"
                  >
                    <PieChart>
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            formatter={(value) => formatCurrency(Number(value))}
                          />
                        }
                      />
                      <Pie
                        data={paymentMethodData}
                        dataKey="value"
                        nameKey="key"
                        innerRadius={52}
                        outerRadius={78}
                        strokeWidth={2}
                      >
                        {paymentMethodData.map((entry) => (
                          <Cell key={entry.key} fill={entry.fill} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                  <div className="grid w-full grid-cols-2 gap-2">
                    <div className="rounded-lg border border-teal-100 bg-teal-50/50 px-3 py-2 text-center">
                      <p className="text-sm font-semibold text-gray-900">{cashShare}%</p>
                      <p className="text-xs text-gray-500">Cash</p>
                    </div>
                    <div className="rounded-lg border border-[#4369a0]/15 bg-[#4369a0]/5 px-3 py-2 text-center">
                      <p className="text-sm font-semibold text-gray-900">{onlineShare}%</p>
                      <p className="text-xs text-gray-500">Online</p>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="py-10 text-center text-sm text-gray-500">No payments this month</p>
              )}
            </SectionCard>
          </div>

          {/* Bottom tables */}
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
            <SectionCard
              title="Recent Payments"
              description="Latest transactions across all members"
              className="xl:col-span-7"
            >
              {recentPayments.length > 0 ? (
                <div className="overflow-hidden rounded-lg border border-gray-200/80">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-200 bg-gray-50/80 hover:bg-gray-50/80">
                        <TableHead className="h-10 text-xs font-semibold tracking-wide text-gray-800">
                          Member
                        </TableHead>
                        <TableHead className="h-10 text-xs font-semibold tracking-wide text-gray-800">
                          Date
                        </TableHead>
                        <TableHead className="h-10 text-xs font-semibold tracking-wide text-gray-800">
                          Progress
                        </TableHead>
                        <TableHead className="h-10 text-xs font-semibold tracking-wide text-gray-800">
                          Type
                        </TableHead>
                        <TableHead className="h-10 text-right text-xs font-semibold tracking-wide text-gray-800">
                          Amount
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentPayments.map((payment) => (
                        <TableRow key={payment.id} className="border-gray-100">
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{payment.memberName}</p>
                              <p className="text-xs text-gray-500">#{payment.memberId}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {formatDateDisplay(payment.paymentDate)}
                          </TableCell>
                          <TableCell className="text-sm tabular-nums text-gray-700">
                            {payment.monthsPaid}/100
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={payment.paymentType.toLowerCase() as Status} />
                          </TableCell>
                          <TableCell className="text-right text-sm font-semibold tabular-nums text-teal-800">
                            {formatCurrency(payment.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center">
                  <p className="text-sm text-gray-500">No payments recorded yet</p>
                </div>
              )}
            </SectionCard>

            <SectionCard
              title="Attention Required"
              description="Active members unpaid this month"
              className="xl:col-span-5"
            >
              {unpaidMembers.length > 0 ? (
                <div className="space-y-2">
                  {unpaidMembers.map(({ member, monthsPaid, amountDue, lastPaidDate }) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-amber-100/80 bg-amber-50/30 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">{member.name}</p>
                        <p className="text-xs text-gray-500">
                          #{member.id} · {monthsPaid}/100 months paid
                        </p>
                        <p className="mt-0.5 text-xs text-gray-600">
                          Last paid:{' '}
                          {lastPaidDate ? formatDateDisplay(lastPaidDate) : 'No payment recorded'}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <StatusBadge status="unpaid" />
                        <p className="mt-1 text-xs font-medium tabular-nums text-amber-800">
                          {formatCurrency(amountDue)} due
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-emerald-200 bg-emerald-50/30 py-12 text-center">
                  <CheckCircle2 className="mb-2 h-8 w-8 text-emerald-600" />
                  <p className="text-sm font-medium text-gray-900">All caught up</p>
                  <p className="mt-1 text-sm text-gray-500">Every active member has paid this month</p>
                </div>
              )}
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}
